import React, { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import {
  Box,
  Card,
  Typography,
  Button,
  Divider,
  Paper,
  List,
  ListItem,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InvoiceNotesTabStyles from '../../../styles/purchaseInvoices/invoiceManagement/InvoiceNotesTabStyles';
import useInvoiceNotes from '../../../hooks/purchaseInvoices/invoiceManagement/useInvoiceNotes';
import DeleteNoteConfirmationDialog from './DeleteNoteConfirmationDialog';

const PASTE_SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'table', 'colgroup', 'col', 'thead', 'tbody', 'tfoot',
    'tr', 'td', 'th',
    'div', 'p', 'span', 'br',
    'b', 'strong', 'i', 'em', 'u',
    'ul', 'ol', 'li',
  ],
  ALLOWED_ATTR: ['style', 'colspan', 'rowspan', 'align', 'valign', 'bgcolor', 'width', 'height', 'cellspacing', 'cellpadding'],
  FORBID_TAGS: ['script', 'style', 'svg', 'math', 'iframe', 'object', 'embed'],
  FORBID_ATTR: ['onerror', 'onclick', 'onload', 'onmouseover', 'onmouseout',
                'onkeydown', 'onkeyup', 'onfocus', 'onblur', 'onchange', 'onsubmit'],
};

// Detects whether a string is HTML content (pasted rich text) vs legacy plain text
const isHtmlContent = (text) => /<[a-z][^>]*>/i.test(text);

// CSS properties we want to promote from Excel's <style> block onto inline style attributes.
// Deliberately narrow: only visual properties that matter for table appearance.
const EXCEL_PROPS_TO_INLINE = new Set([
  'background', 'background-color', 'color',
  'font-weight', 'font-style', 'text-align', 'vertical-align',
  'border', 'border-top', 'border-bottom', 'border-left', 'border-right',
]);

// Returns a simple CSS color value (hex, named, rgb/rgba), or null for gradients/urls/none.
const extractSimpleColor = (val) => {
  if (!val) return null;
  const v = val.trim();
  if (v === 'none' || v === 'transparent' || v === 'inherit' || v === 'initial') return null;
  if (/^#[0-9a-fA-F]{3,8}$/.test(v)) return v;
  if (/^[a-zA-Z]+$/.test(v)) return v;
  if (/^rgba?\s*\([^)]+\)$/.test(v)) return v;
  return null;
};

/**
 * Excel clipboard HTML stores cell colors as CSS classes in a <style> block, e.g.:
 *   <style><!-- .xl65 { background: #FFFF00; } --></style>
 *   <td class="xl65">Yellow</td>
 *
 * Our sanitizer (correctly) strips <style> blocks and class attributes, which loses
 * the colors. This function resolves them first: it reads the class rules, applies
 * matching properties as inline style="" on each element, then returns the body HTML.
 * After this call DOMPurify can safely strip <style> + class without losing colors.
 *
 * Email-client fixes applied here so they persist through save → email render:
 *  - background shorthand → also set background-color (Outlook ignores the shorthand)
 *  - bgcolor attribute added on <td>/<th> (survives even when CSS is stripped entirely)
 *  - cellspacing/cellpadding="0" on <table> (border-collapse ignored by Outlook/Word)
 */
const inlineExcelStyles = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  // Build className → { prop: value } map from all <style> elements.
  // Excel wraps the entire block in an HTML comment <!-- ... --> — strip those markers.
  const styleMap = {};
  doc.querySelectorAll('style').forEach((styleEl) => {
    const cssText = styleEl.textContent
      .replace(/^\s*<!--/, '')
      .replace(/-->\s*$/, '');
    const ruleRegex = /\.([\w-]+)\s*\{([^}]+)\}/g;
    let m;
    while ((m = ruleRegex.exec(cssText)) !== null) {
      const cls = m[1];
      if (!styleMap[cls]) styleMap[cls] = {};
      m[2].split(';').forEach((decl) => {
        const ci = decl.indexOf(':');
        if (ci === -1) return;
        const prop = decl.slice(0, ci).trim().toLowerCase();
        const val  = decl.slice(ci + 1).trim();
        if (prop && val) styleMap[cls][prop] = val;
      });
    }
  });

  // Apply matched class rules as inline styles (class rules are additive;
  // any existing inline style on the element wins — it comes last in the string
  // so sanitize-html/DOMPurify will keep it when there are duplicates).
  if (Object.keys(styleMap).length > 0) {
    doc.querySelectorAll('[class]').forEach((el) => {
      const promoted = {};
      el.getAttribute('class').split(/\s+/).forEach((cls) => {
        const rules = styleMap[cls];
        if (!rules) return;
        Object.entries(rules).forEach(([prop, val]) => {
          if (EXCEL_PROPS_TO_INLINE.has(prop)) promoted[prop] = val;
        });
      });
      if (Object.keys(promoted).length === 0) return;

      // Expand background shorthand → also set background-color so Outlook renders colors.
      if (promoted['background'] && !promoted['background-color']) {
        const color = extractSimpleColor(promoted['background']);
        if (color) promoted['background-color'] = color;
      }

      const fromClass = Object.entries(promoted).map(([p, v]) => `${p}:${v}`).join(';');
      const existing  = el.getAttribute('style') || '';
      el.setAttribute('style', existing ? `${fromClass};${existing}` : fromClass);
    });
  }

  // For every <td>/<th>: ensure both background and background-color are present
  // (whichever exists, derive the other), then add bgcolor attribute as a hard
  // fallback for Outlook's Word rendering engine and legacy email clients.
  doc.querySelectorAll('td, th').forEach((cell) => {
    const style = cell.getAttribute('style') || '';
    const bgColorM = style.match(/(?:^|;)\s*background-color\s*:\s*([^;]+)/i);
    const bgM      = style.match(/(?:^|;)\s*background\s*:\s*([^;]+)/i);

    const color = extractSimpleColor(
      ((bgColorM && bgColorM[1]) || (bgM && bgM[1]) || '').trim()
    );
    if (!color) return;

    let newStyle = style;
    if (!bgColorM) newStyle = `background-color:${color};${newStyle}`;
    if (!bgM)      newStyle = `background:${color};${newStyle}`;
    if (newStyle !== style) cell.setAttribute('style', newStyle);

    if (!cell.hasAttribute('bgcolor')) cell.setAttribute('bgcolor', color);
  });

  // Add cellspacing/cellpadding="0" on every <table> — border-collapse:collapse is
  // ignored by Outlook's Word engine; these HTML attributes are the reliable fallback.
  doc.querySelectorAll('table').forEach((table) => {
    if (!table.hasAttribute('cellspacing')) table.setAttribute('cellspacing', '0');
    if (!table.hasAttribute('cellpadding')) table.setAttribute('cellpadding', '0');
  });

  return doc.body.innerHTML;
};

const formatDate = (dateString) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('he-IL', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    });
  } catch (e) {
    return dateString;
  }
};

const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

const getFileIcon = (fileName) => {
  const extension = fileName.split('.').pop().toLowerCase();
  if (extension === 'pdf') return <PictureAsPdfIcon sx={{ color: '#d32f2f' }} />;
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(extension)) return <ImageIcon sx={{ color: '#1976d2' }} />;
  return <DescriptionIcon sx={{ color: '#757575' }} />;
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const InvoiceNotesTab = ({ invoice, onSaveNotes, onNavigateToFile, onNavigateToSpecificFile }) => {
  const {
    notes,
    setNotes,
    isSaving,
    isDeleting,
    handleSave,
    openDeleteConfirmation,
    showDeleteConfirmation,
    closeDeleteConfirmation,
    confirmDeleteNote,
    previousNotes,
    selectedFiles,
    isUploadingFiles,
    uploadError,
    handleFileSelect,
    handleRemoveFile
  } = useInvoiceNotes(invoice, onSaveNotes);

  const editorRef = useRef(null);
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);

  // Sync DOM → cleared state when hook resets notes to '' after a successful save
  useEffect(() => {
    if (notes === '' && editorRef.current && editorRef.current.innerHTML) {
      editorRef.current.innerHTML = '';
      setIsEditorEmpty(true);
    }
  }, [notes]);

  const handleEditorInput = useCallback(() => {
    const html = editorRef.current.innerHTML;
    const hasContent = !!(editorRef.current.textContent || '').trim();
    setIsEditorEmpty(!hasContent);
    setNotes(html);
  }, [setNotes]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    range.deleteContents();

    if (html) {
      // Resolve Excel class-based colors → inline styles before DOMPurify strips <style>+class
      const inlined = inlineExcelStyles(html);
      const clean = DOMPurify.sanitize(inlined, PASTE_SANITIZE_CONFIG);
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = clean;
      const fragment = document.createDocumentFragment();
      while (tempDiv.firstChild) fragment.appendChild(tempDiv.firstChild);
      range.insertNode(fragment);
    } else {
      range.insertNode(document.createTextNode(text));
    }

    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);

    const newHtml = editorRef.current.innerHTML;
    setNotes(newHtml);
    setIsEditorEmpty(!(editorRef.current.textContent || '').trim());
  }, [setNotes]);

  const handleAttachmentClick = useCallback((attachment) => {
    if (onNavigateToSpecificFile) {
      onNavigateToSpecificFile(attachment.fileName);
    } else if (onNavigateToFile) {
      onNavigateToFile();
    }
    // Commenting out automatic opening in new tab to allow in-app navigation
    // if (attachment.downloadUrl) {
    //   window.open(attachment.downloadUrl, '_blank');
    // }
  }, [onNavigateToFile, onNavigateToSpecificFile]);

  const processedNotes = useMemo(() => {
    return previousNotes.map((note, index) => ({
      ...note,
      uniqueId: note.timestamp || `note-${index}`,
      isAttachmentsOnly: note.isAttachmentOnly || 
                        ((!note.text || note.text.trim() === '') && note.attachments && note.attachments.length > 0) ||
                        (note.text && note.text.startsWith('[צרופות:') && note.text.endsWith(']'))
    }));
  }, [previousNotes]);

  const sortedNotes = useMemo(() => [...processedNotes].reverse(), [processedNotes]);

  const RegularNoteDisplay = useCallback(({ note, index }) => (
    <Box sx={{ position: 'relative' }}>
      <Box sx={InvoiceNotesTabStyles.noteHeader}>
        <Box sx={InvoiceNotesTabStyles.userInfoContainer}>
          <Avatar sx={InvoiceNotesTabStyles.userAvatar}>
            {getInitials(note.user)}
          </Avatar>
          <Typography variant="subtitle2" sx={InvoiceNotesTabStyles.noteUser}>
            {note.user || 'לא ידוע'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={InvoiceNotesTabStyles.noteDate}>
            {formatDate(note.timestamp)}
          </Typography>
          <Tooltip title="מחק הערה">
            <IconButton 
              size="small" 
              color="error" 
              onClick={() => openDeleteConfirmation(processedNotes.length - 1 - index)}
              disabled={isDeleting}
              sx={{ 
                opacity: 0.7, 
                '&:hover': { opacity: 1 },
                transition: 'opacity 0.2s'
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={InvoiceNotesTabStyles.noteContent}>
        {note.text && note.text.trim() && !note.isAttachmentsOnly && (
          isHtmlContent(note.text) ? (
            <Box
              sx={{
                ...InvoiceNotesTabStyles.noteText,
                whiteSpace: 'normal',
                '& table': { borderCollapse: 'collapse', margin: '4px 0', maxWidth: '100%' },
                '& td, & th': { border: '1px solid #ccc', padding: '4px 8px', minWidth: '30px' },
              }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note.text, PASTE_SANITIZE_CONFIG) }}
            />
          ) : (
            <Typography variant="body2" sx={InvoiceNotesTabStyles.noteText}>
              {note.text.split('\n').map((line, lineIndex) => {
                const isEmailHeader = line.trim() === 'חשבונית זו הועלתה דרך המייל' || line.trim() === 'תוכן המייל:';
                return (
                  <React.Fragment key={lineIndex}>
                    {isEmailHeader ? (
                      <Box
                        component="span"
                        sx={{
                          fontWeight: 700,
                          color: '#1976d2',
                          fontSize: '1.05em',
                          display: 'block',
                          marginBottom: '8px'
                        }}
                      >
                        {line}
                      </Box>
                    ) : (
                      line
                    )}
                    {lineIndex < note.text.split('\n').length - 1 && <br />}
                  </React.Fragment>
                );
              })}
            </Typography>
          )
        )}
        {note.attachments && note.attachments.length > 0 && (
          <Box sx={{ mt: note.text && note.text.trim() && !note.isAttachmentsOnly ? 2 : 0 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {note.attachments.map((attachment, attachIndex) => (
                <Tooltip 
                  key={`${note.uniqueId}-regular-attachment-${attachIndex}`}
                  title="לחץ כדי לעבור לטאב הקבצים ולצפות בצרופה"
                  arrow
                >
                  <Chip
                    icon={<FolderOpenIcon fontSize="small" />}
                    label={attachment.fileName}
                    variant="outlined"
                    size="small"
                    onClick={() => handleAttachmentClick(attachment)}
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      borderColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '& .MuiChip-icon': { color: 'white' }
                      }
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  ), [handleAttachmentClick, openDeleteConfirmation, isDeleting, processedNotes.length]);

  return (
    <Box sx={InvoiceNotesTabStyles.container}>
      <Card sx={InvoiceNotesTabStyles.card}>
        <Typography variant="h6" gutterBottom color="primary" sx={InvoiceNotesTabStyles.mainTitle}>
          הערות וצרופות לחשבונית
        </Typography>
        
        {sortedNotes.length > 0 && (
          <>
            <Box sx={InvoiceNotesTabStyles.previousNotesHeader}>
              <ChatBubbleIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="subtitle1" color="primary" fontWeight="bold">
                היסטוריית הערות וצרופות
              </Typography>
              <Chip 
                label={sortedNotes.length} 
                size="small" 
                color="primary" 
                sx={InvoiceNotesTabStyles.noteCountChip} 
              />
            </Box>
            <Paper variant="outlined" sx={InvoiceNotesTabStyles.previousNotesContainer}>
              <List disablePadding>
                {sortedNotes.map((note, index) => (
                  <ListItem 
                    key={`list-item-${note.uniqueId}`}
                    sx={InvoiceNotesTabStyles.previousNoteItem}
                    divider={index !== sortedNotes.length - 1}
                  >
                    <Box sx={InvoiceNotesTabStyles.noteWrapper}>
                      <RegularNoteDisplay note={note} index={index} />
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Paper>
            <Divider sx={InvoiceNotesTabStyles.sectionDivider} />
          </>
        )}
        
        <Box sx={InvoiceNotesTabStyles.addNoteSection}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={InvoiceNotesTabStyles.newNotesLabel}>
            הוספת הערה חדשה
          </Typography>
          
          <Paper variant="outlined" sx={InvoiceNotesTabStyles.newNoteContainer}>
            <Box sx={{ position: 'relative' }}>
              <Box
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                dir="rtl"
                onInput={handleEditorInput}
                onPaste={handlePaste}
                sx={{
                  minHeight: '100px',
                  padding: '16px',
                  fontSize: '0.95rem',
                  fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                  lineHeight: 1.6,
                  color: '#374151',
                  backgroundColor: '#ffffff',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  outline: 'none',
                  cursor: 'text',
                  overflowY: 'auto',
                  wordBreak: 'break-word',
                  transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                  '&:focus': {
                    borderColor: '#1976d2',
                    boxShadow: '0 0 0 4px rgba(25, 118, 210, 0.1)',
                  },
                  '&:hover:not(:focus-within)': {
                    borderColor: '#9ca3af',
                  },
                  '& table': { borderCollapse: 'collapse', margin: '4px 0', maxWidth: '100%' },
                  '& td, & th': { border: '1px solid #ccc', padding: '4px 8px', minWidth: '30px' },
                }}
              />
              {isEditorEmpty && (
                <Typography
                  sx={{
                    position: 'absolute',
                    top: '16px',
                    left: '16px',
                    color: 'rgba(0,0,0,0.38)',
                    fontSize: '0.95rem',
                    pointerEvents: 'none',
                    userSelect: 'none',
                    fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                  }}
                >
                  הקלד את ההערה שלך כאן...
                </Typography>
              )}
            </Box>
            
            <Box sx={{
              mt: 2,
              p: 2,
              border: '1px dashed #ccc',
              borderRadius: 2,
              backgroundColor: '#fafafa'
            }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                <AttachFileIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                צרופות
              </Typography>
              
              <input
                accept="*/*"
                style={{ display: 'none' }}
                id="attachment-upload"
                multiple
                type="file"
                onChange={handleFileSelect}
                disabled={isUploadingFiles}
              />
              <label htmlFor="attachment-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  disabled={isUploadingFiles}
                  sx={{ mb: 2 }}
                >
                  בחר קבצים לצרופה
                </Button>
              </label>

              {uploadError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {uploadError}
                </Alert>
              )}

              {selectedFiles.length > 0 && (
                <Box sx={{ maxHeight: '200px', overflow: 'auto' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    קבצים נבחרים ({selectedFiles.length}):
                  </Typography>
                  {selectedFiles.map((file, index) => (
                    <Box key={`selected-file-${index}-${file.name}`} sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      mb: 1,
                      backgroundColor: 'white'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        {getFileIcon(file.name)}
                        <Typography variant="body2" sx={{ ml: 1, mr: 2, fontWeight: 500 }}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {formatFileSize(file.size)}
                        </Typography>
                        {!file.uploaded && !file.uploading && (
                          <Chip 
                            label="ממתין להעלאה" 
                            color="warning" 
                            variant="outlined"
                            size="small" 
                            sx={{ ml: 1 }} 
                          />
                        )}
                      </Box>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveFile(index)}
                        disabled={file.uploading}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            
            <Box sx={InvoiceNotesTabStyles.buttonsContainer}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleSave}
                disabled={isSaving || isUploadingFiles || (isEditorEmpty && selectedFiles.length === 0)}
                sx={InvoiceNotesTabStyles.saveButton}
              >
                {isSaving ? 'שומר...' :
                 isUploadingFiles ? 'מעלה קבצים...' :
                 !isEditorEmpty && selectedFiles.length > 0 ? 'שמור הערה וצרופות' :
                 !isEditorEmpty ? 'שמור הערה' :
                 selectedFiles.length > 0 ? 'שמור צרופות' :
                 'שמור הערה וצרופות'}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Card>
      
      <DeleteNoteConfirmationDialog 
        open={showDeleteConfirmation}
        onClose={closeDeleteConfirmation}
        onConfirm={confirmDeleteNote}
        isDeleting={isDeleting}
      />
    </Box>
  );
};

export default InvoiceNotesTab;